/**
 * Format lamports to SOL with thousands separators
 * 
 * @param {string|number|null|undefined} lamports - Lamports value
 * @returns {string} - Formatted SOL value
 */
export function formatLamports(lamports) {
  if (lamports === null || lamports === undefined || lamports === '') {
    return '0';
  }

  const lamportsValue = typeof lamports === 'string' ? parseInt(lamports, 10) : lamports;

  if (!Number.isFinite(lamportsValue)) {
    return '0';
  }

  const sol = lamportsValue / 1_000_000_000;
  return sol.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 9,
  });
}

/**
 * Format token amount with decimals
 * 
 * @param {string|number|null|undefined} amountAtomic - Atomic token amount
 * @param {number} decimals - Token decimals (default 9)
 * @returns {string} - Formatted token amount
 */
export function formatTokenAmount(amountAtomic, decimals = 9) {
  if (amountAtomic === null || amountAtomic === undefined || amountAtomic === '') {
    return '0';
  }

  const normalizedAmount = String(amountAtomic).trim();
  const normalizedDecimals = Number.parseInt(decimals ?? 9, 10);

  if (!/^\d+$/.test(normalizedAmount)) {
    return '0';
  }

  if (!Number.isFinite(normalizedDecimals) || normalizedDecimals <= 0) {
    return normalizedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const wholePart =
    normalizedAmount.length > normalizedDecimals
      ? normalizedAmount.slice(0, -normalizedDecimals)
      : '0';
  const fractionalPart = normalizedAmount.slice(-normalizedDecimals).padStart(normalizedDecimals, '0');
  const trimmedFractionalPart = fractionalPart.replace(/0+$/, '');
  const formattedWholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (!trimmedFractionalPart) {
    return formattedWholePart;
  }

  return `${formattedWholePart}.${trimmedFractionalPart}`;
}

/**
 * Truncate a public key or address for display
 * 
 * @param {string} address - Full address
 * @param {number} startChars - Characters to show at start (default 4)
 * @param {number} endChars - Characters to show at end (default 4)
 * @returns {string} - Truncated address
 */
export function truncateAddress(address, startChars = 4, endChars = 4) {
  if (!address || typeof address !== 'string') {
    return '';
  }

  if (address.length <= startChars + endChars) {
    return address;
  }

  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
}
